import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getTrySessionId } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useMessageStream } from '@/lib/useMessageStream';
import { AgentAvatar } from '@/components/AgentAvatar';
import { CHAT } from '@/constants/testIds';
import { Send, Lock, Plus, ArrowLeft, AlertTriangle, GitBranch, Share2, Copy, Linkedin, Twitter, Sparkles, Wand2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { UpgradeModal } from '@/components/UpgradeModal';

// Category-specific fallback prompt suggestions (used when the agent has no starter_prompt
// or when we want additional one-click ideas).
const CATEGORY_PROMPTS = {
  'Sales & Lead-Gen': [
    'Draft me a 4-touch outbound sequence for RevOps leaders at $10-50M ARR SaaS.',
    'Roleplay a skeptical CFO objecting on price. Push me hard.',
    'Score this lead: Founder at 40-person B2B, $8M ARR, drowning in outbound. Booked demo Thursday.',
  ],
  'Marketing & Content': [
    'Write me a landing page for an AI product priced $49/mo targeting founders.',
    'Turn this into 5 LinkedIn posts: "We hit $12k MRR in week 1 with 0 ad spend."',
    'Build an SEO brief for "AI agents for revenue operations". Target RevOps managers.',
  ],
  'Paid Ads': [
    'Give me 5 Meta ads, 3 LinkedIn ads, 3 Google ads for RevCore AI targeting RevOps leads.',
    'Write 5 hooks for a cold audience — angle: pain, desire, authority, FOMO.',
  ],
  'Customer Support': [
    'Reply to: "Billing failed 3 times this week. I\u2019ll cancel if it happens again."',
    'Draft a churn win-back for a 6-month Pro user who says "we\u2019re not using it enough".',
  ],
  'Finance': [
    'Draft a Day 7/14/21/30 collections ladder for a $8,400 invoice, 22 days overdue.',
  ],
  'Operations & Automation': [
    'Build a SOP for onboarding new SaaS customers from signup to first "aha moment".',
    'Extract action items + Slack recap from these meeting notes: [paste here]',
  ],
  'Research & Strategy': [
    'Competitive brief: AI agent platforms for GTM. Us vs Copy.ai vs Regie.ai vs Clay.',
    'Critique this pitch deck slide by slide: [paste deck summary]',
    'Rewrite these YC application answers to earn an interview: [paste answers]',
  ],
  'HR & Recruiting': [
    'Write a JD + 3 recruiter DMs for a Founding RevOps Lead role, $180-220k + 1% equity.',
  ],
  'Data & Analytics': [
    'Write PostgreSQL: net revenue retention by cohort month over the last 6 months.',
  ],
};

function buildSuggestions(agent) {
  if (!agent) return [];
  const list = [];
  if (agent.starter_prompt) list.push({ label: 'Try the demo prompt', prompt: agent.starter_prompt });
  const catList = CATEGORY_PROMPTS[agent.category] || [];
  catList.forEach((p) => list.push({ label: p.length > 74 ? p.slice(0, 72) + '\u2026' : p, prompt: p }));
  return list.slice(0, 5);
}

// ---------- Sub-components (extracted to keep Chat() under threshold) ----------

function Dot({ d = 0 }) {
  return <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#39FF14]" style={{ animation: `pulse 1.2s ${d}ms infinite ease-in-out` }} />;
}

function ChatHeader({ agent, anonRemaining, isAuthed, onBack, onFork, onShare }) {
  return (
    <header className="p-4 border-b border-border/60 flex items-center gap-3">
      <button onClick={onBack} className="btn-ghost p-1.5 rounded"><ArrowLeft className="h-4 w-4"/></button>
      {agent && <>
        <AgentAvatar name={agent.name} size={36} />
        <div className="min-w-0">
          <div className="display font-semibold truncate">{agent.name}</div>
          <div className="text-[11px] text-white/50 truncate">{agent.role} • <span className="mono">{agent.model_provider}/{agent.model_name}</span></div>
        </div>
      </>}
      <div className="ml-auto flex items-center gap-1.5">
        <button onClick={onFork} title="Fork into Builder" className="btn-ghost p-1.5 rounded inline-flex items-center gap-1 text-xs" data-testid="chat-fork-button">
          <GitBranch className="h-4 w-4 text-[#39FF14]" />
        </button>
        <button onClick={onShare} title="Copy share link" className="btn-ghost p-1.5 rounded inline-flex items-center gap-1 text-xs" data-testid="chat-share-button">
          <Share2 className="h-4 w-4 text-[#FF6B1A]" />
        </button>
        {!isAuthed && (
          <div data-testid="chat-anon-remaining" className="text-[11px] hairline px-2 py-1 rounded-full text-white/60 inline-flex items-center gap-1 ml-1">
            <Lock className="h-3 w-3"/> {anonRemaining ?? '2'} free {anonRemaining === 1 ? 'try' : 'tries'} left
          </div>
        )}
      </div>
    </header>
  );
}

function MessageActions({ content, agentName }) {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };
  const site = typeof window !== 'undefined' ? window.location.origin : 'https://revcoreai.com';
  const watermark = `\n\n\u2014 Generated with RevCore AI (${agentName || 'agent'}) \u00b7 ${site}`;
  const shareText = (content.length > 900 ? content.slice(0, 900) + '\u2026' : content) + watermark;
  const onLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(site)}`;
    navigator.clipboard.writeText(shareText).catch(() => {});
    toast.success('Response copied \u2014 paste it into your LinkedIn post');
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const onTwitter = () => {
    const text = content.length > 240 ? content.slice(0, 238) + '\u2026' : content;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text + '\n\nvia @revcoreai')}&url=${encodeURIComponent(site)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  return (
    <div className="flex items-center gap-1 mt-2 -ml-1">
      <button onClick={onCopy} data-testid="chat-msg-copy" className="btn-ghost inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-white/55 hover:text-white">
        <Copy className="h-3 w-3" /> Copy
      </button>
      <button onClick={onLinkedIn} data-testid="chat-msg-linkedin" className="btn-ghost inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-white/55 hover:text-[#39FF14]">
        <Linkedin className="h-3 w-3" /> LinkedIn
      </button>
      <button onClick={onTwitter} data-testid="chat-msg-twitter" className="btn-ghost inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] text-white/55 hover:text-[#FF6B1A]">
        <Twitter className="h-3 w-3" /> X
      </button>
    </div>
  );
}

function EmptyStateSuggestions({ agent, onPickPrompt }) {
  const suggestions = buildSuggestions(agent);
  return (
    <div className="h-full flex items-center justify-center px-4">
      <div className="max-w-2xl w-full text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full hairline text-[11px] text-white/70 mb-5">
          <span className="h-1.5 w-1.5 rounded-full bg-[#39FF14] pulse-green" />
          {agent?.name || 'Your agent'} is warmed up and ready
        </div>
        <div className="display text-2xl font-semibold">What should {agent?.name?.split(' ')[0] || 'we'} tackle first?</div>
        <p className="text-sm text-white/55 mt-2 max-w-lg mx-auto">
          {agent?.description ? agent.description : 'Ask anything in their wheelhouse. Real LLM responses stream in real time.'}
        </p>
        {suggestions.length > 0 && (
          <div className="mt-6 grid gap-2 text-left">
            {suggestions.map((s, i) => (
              <button
                key={`sugg-${i}`}
                onClick={() => onPickPrompt(s.prompt)}
                data-testid={`chat-suggestion-${i}`}
                className="w-full text-left hairline hover:border-[#FF6B1A]/50 hover:bg-[#FF6B1A]/5 rounded-xl px-4 py-3 text-sm text-white/85 transition-colors group inline-flex items-start gap-3"
              >
                <Wand2 className="h-4 w-4 text-[#39FF14] mt-0.5 shrink-0 group-hover:text-[#39FF14]" />
                <span className="flex-1">{s.label}</span>
                <Sparkles className="h-3.5 w-3.5 text-white/25 group-hover:text-[#FF6B1A] mt-0.5 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageList({ agent, messages, sending, streamingText, listEndRef, onPickPrompt }) {
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {messages.length === 0 && !sending && (
        <EmptyStateSuggestions agent={agent} onPickPrompt={onPickPrompt} />
      )}
      {messages.map((m, idx) => {
        const isLast = idx === messages.length - 1;
        return (
          <motion.div key={m.message_id} data-testid={CHAT.message} data-role={m.role}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={`flex gap-3 max-w-3xl ${m.role === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto'}`}>
            {m.role === 'assistant'
              ? <AgentAvatar name={agent?.name || '?'} size={32} />
              : <div className="h-8 w-8 rounded-xl bg-white/5 grid place-items-center text-xs text-white/70 mono">YOU</div>}
            <div className="min-w-0 max-w-full">
              <div className={`p-3.5 rounded-xl border text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === 'user' ? 'bg-white/5 border-white/8' : 'bg-card/60 border-border/60'
              }`}>{m.content}</div>
              {m.role === 'assistant' && (!sending || !isLast) && (
                <MessageActions content={m.content} agentName={agent?.name} />
              )}
            </div>
          </motion.div>
        );
      })}
      {(streamingText || (sending && messages.length > 0)) && (
        <div className="flex gap-3 max-w-3xl mr-auto">
          <AgentAvatar name={agent?.name || '?'} size={32} />
          <div className="p-3.5 rounded-xl border border-border/60 bg-card/60 text-sm leading-relaxed whitespace-pre-wrap">
            {streamingText
              ? <>{streamingText}<span className="inline-block w-2 h-4 bg-[#FF6B1A] ml-0.5 align-middle animate-pulse" /></>
              : <span className="inline-flex gap-1"><Dot/><Dot d={150}/><Dot d={300}/></span>
            }
          </div>
        </div>
      )}
      <div ref={listEndRef} />
    </div>
  );
}

function InputArea({ input, setInput, sending, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="p-3 border-t border-border/60 flex gap-2 items-end">
      <textarea
        data-testid={CHAT.input}
        rows={1}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(e); }
        }}
        placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
        className="input flex-1 min-h-[44px] max-h-32 resize-none"
      />
      <button type="submit" data-testid={CHAT.send} disabled={!input.trim() || sending}
        className="btn-primary rounded-xl px-4 py-3 inline-flex items-center gap-1.5 disabled:opacity-50">
        <Send className="h-4 w-4"/> {sending ? 'Streaming…' : 'Send'}
      </button>
    </form>
  );
}

function ConversationsSidebar({ items, conversationId, onSelect, onNew }) {
  return (
    <aside data-testid={CHAT.list} className="card-base p-3 flex flex-col gap-2 overflow-hidden">
      <div className="flex items-center justify-between px-2 py-1">
        <span className="text-[10px] uppercase tracking-widest text-[#FF6B1A]">// chats</span>
        <button onClick={onNew} className="btn-ghost text-xs rounded px-2 py-1 inline-flex items-center gap-1"><Plus className="h-3 w-3"/> New</button>
      </div>
      <div className="overflow-y-auto flex-1 space-y-1 pr-1">
        {items.map((c) => (
          <button key={c.conversation_id} onClick={() => onSelect(c.conversation_id)}
            className={`w-full text-left p-2 rounded-lg text-xs transition-colors ${
              conversationId === c.conversation_id ? 'bg-white/5 border border-[#FF6B1A]/30' : 'hover:bg-white/3 border border-transparent'
            }`}
          >
            <div className="truncate text-white/85">{c.title || 'Conversation'}</div>
            <div className="text-[10px] text-white/40 mt-0.5">{c.agent_name}</div>
          </button>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-white/40 px-2">No conversations yet.</div>
        )}
      </div>
    </aside>
  );
}

function AgentInfoRail({ agent, isAuthed, onFork, onShare }) {
  if (!agent) return <div className="text-white/60 text-sm">Loading…</div>;
  return (
    <>
      <div className="flex items-center gap-3">
        <AgentAvatar name={agent.name} size={44} />
        <div>
          <div className="display font-semibold">{agent.name}</div>
          <div className="text-xs text-white/55">{agent.category}</div>
        </div>
      </div>
      <p className="text-xs text-white/65 mt-3">{agent.description}</p>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mt-5 mb-1">model</div>
      <div className="mono text-xs text-white/80">{agent.model_provider}/{agent.model_name}</div>
      <button onClick={onFork} className="btn-secondary w-full mt-5 rounded-xl px-3 py-2 text-xs inline-flex items-center justify-center gap-1">
        <GitBranch className="h-3.5 w-3.5" /> Fork into Builder
      </button>
      <button onClick={onShare} className="btn-ghost w-full mt-2 rounded-xl px-3 py-2 text-xs inline-flex items-center justify-center gap-1">
        <Share2 className="h-3.5 w-3.5" /> Copy share link
      </button>
      {!isAuthed && (
        <div className="mt-5 hairline rounded-xl p-3 text-xs text-white/65 bg-[#FF6B1A]/5">
          <AlertTriangle className="h-4 w-4 text-[#FF6B1A] mb-1"/>
          You're chatting anonymously. Each agent gives you 2 free tries — <Link to="/login" className="text-[#39FF14] underline">sign up free</Link> to unlock unlimited chat with all 18 agents.
        </div>
      )}
    </>
  );
}

// ---------- Main Chat() ----------

export default function Chat() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const agentId = params.get('agent');
  const convFromUrl = params.get('conv');
  const prefillFromUrl = params.get('prefill');
  const [conversationId, setConversationId] = useState(convFromUrl || null);
  const [input, setInput] = useState(prefillFromUrl ? decodeURIComponent(prefillFromUrl) : '');
  const [anonRemaining, setAnonRemaining] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const listEndRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getTrySessionId is module-level, intentionally stable
  const trySessionId = useMemo(() => getTrySessionId(), []);

  const { data: agent } = useQuery({
    queryKey: ['agent', agentId],
    queryFn: async () => (await api.get(`/agents/${agentId}`)).data,
    enabled: !!agentId,
  });

  // Fetch per-agent anonymous remaining so header shows correct count on load.
  useQuery({
    queryKey: ['anon-remaining-chat', agentId, trySessionId, !!user],
    queryFn: async () => {
      if (!agentId || user) return null;
      const r = await api.get(`/agents/${agentId}/anon-remaining?try_session_id=${trySessionId}`);
      if (typeof r.data?.remaining === 'number') {
        setAnonRemaining(r.data.remaining);
      }
      return r.data;
    },
    enabled: !!agentId && !user,
  });

  const { data: convs } = useQuery({
    queryKey: ['conversations', user?.user_id || trySessionId],
    queryFn: async () => {
      const url = user ? '/conversations' : `/conversations?try_session_id=${trySessionId}`;
      return (await api.get(url)).data;
    },
  });

  const { data: convData } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: async () => {
      const url = user
        ? `/conversations/${conversationId}`
        : `/conversations/${conversationId}?try_session_id=${trySessionId}`;
      return (await api.get(url)).data;
    },
    enabled: !!conversationId,
  });
  const messages = useMemo(() => convData?.messages || [], [convData]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- listEndRef is a ref; scrollIntoView is a DOM method
  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, conversationId]);

  // ----- Streaming via extracted hook -----
  const handleStart = useCallback((convId) => {
    if (!conversationId) setConversationId(convId);
  }, [conversationId]);
  const handleCap = useCallback((detail) => {
    setShowUpgrade(true);
    toast.error(detail || 'Free limit reached');
  }, []);
  const handleDone = useCallback(({ conversationId: convId, anonRemaining: rem }) => {
    if (typeof rem === 'number') setAnonRemaining(rem);
    if (convId) {
      qc.invalidateQueries({ queryKey: ['conversation', convId] });
      qc.invalidateQueries({ queryKey: ['conversations'] });
    }
    if (user) qc.invalidateQueries({ queryKey: ['usage-me'] });
  }, [qc, user]);

  const { send, sending, streamingText } = useMessageStream({
    onStart: handleStart,
    onCap: handleCap,
    onDone: handleDone,
  });

  const onSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending || !agentId) return;
    const userText = text;
    setInput('');
    const result = await send({
      agentId,
      message: userText,
      conversationId,
      trySessionId: user ? null : trySessionId,
    });
    if (result?.capped) setInput(userText); // restore so they can retry post-upgrade
  };

  // Fired when user clicks a starter suggestion pill: auto-sends the prompt.
  const onPickPrompt = useCallback(async (prompt) => {
    if (!prompt || sending || !agentId) return;
    setInput('');
    const result = await send({
      agentId,
      message: prompt,
      conversationId,
      trySessionId: user ? null : trySessionId,
    });
    if (result?.capped) setInput(prompt);
  }, [send, agentId, conversationId, sending, trySessionId, user]);

  const onShare = useCallback(async () => {
    if (!agent?.agent_id) return;
    const url = `${window.location.origin}/share/${agent.agent_id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Share link copied');
    } catch {
      toast.message('Share link', { description: url });
    }
  }, [agent]);
  const onFork = useCallback(() => {
    if (!agentId) return;
    navigate(`/builder?fork=${agentId}`);
  }, [agentId, navigate]);

  if (!agentId) {
    return <div className="max-w-5xl mx-auto px-4 pt-12 text-white/60">Pick an agent from the <Link to="/marketplace" className="text-[#FF6B1A] underline">marketplace</Link>.</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10 grid lg:grid-cols-[260px_minmax(0,1fr)_300px] gap-4 h-[calc(100vh-4rem-6rem)] min-h-[640px]">
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        isAnonymous={!user}
        trySessionId={trySessionId}
      />
      <ConversationsSidebar
        items={convs?.items || []}
        conversationId={conversationId}
        onSelect={setConversationId}
        onNew={() => setConversationId(null)}
      />
      <section className="card-base flex flex-col overflow-hidden">
        <ChatHeader
          agent={agent}
          anonRemaining={anonRemaining}
          isAuthed={!!user}
          onBack={() => navigate(-1)}
          onFork={onFork}
          onShare={onShare}
        />
        <MessageList
          agent={agent}
          messages={messages}
          sending={sending}
          streamingText={streamingText}
          listEndRef={listEndRef}
          onPickPrompt={onPickPrompt}
        />
        <InputArea input={input} setInput={setInput} sending={sending} onSubmit={onSend} />
      </section>
      <aside className="card-base p-4 overflow-y-auto hidden lg:block">
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// agent</div>
        <AgentInfoRail agent={agent} isAuthed={!!user} onFork={onFork} onShare={onShare} />
      </aside>
    </div>
  );
}
